import React, { useState, useEffect, useRef, useCallback } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { useSettings } from "../contexts/SettingsContext";

// 2026-02-19 (Dave) — polish shared with LoginScreen + reset-with-token:
// after toggling password visibility, park the cursor at position 0 so
// long passwords aren't clipped to the right after the type-attribute
// swap. Kept as a module-scope helper so all three fields in this modal
// use identical semantics.
function jumpCursorToStart(ref) {
  requestAnimationFrame(() => {
    const el = ref?.current;
    if (!el) return;
    try {
      el.focus();
      el.setSelectionRange(0, 0);
      el.scrollLeft = 0;
    } catch { /* setSelectionRange throws on some input types — safe to ignore */ }
  });
}

// Change-password modal.
//
// TWO MODES (2026-02-15 · Dave):
//   • `mode="forced"` (default) — rendered by AppGate when the signed-in
//     user carries `must_change_password === true`. Non-dismissable (no
//     Esc, no backdrop click, no Cancel). Only exits are (a) success or
//     (b) "Sign out instead".
//   • `mode="manual"` — user-initiated rotation from the User menu.
//     Dismissable via Esc / backdrop / Cancel button. Header + copy
//     shift to reflect the voluntary context. Success fires `onSuccess`
//     (parent shows a toast and closes the modal).
//
// Layout mirrors StatementOfPurposeModal for visual consistency.
// Password policy sourced live from platform_settings.
//
// Props:
//   open           — controls visibility.
//   mode           — "forced" | "manual" (default "forced").
//   onSubmit       — async (currentPw, newPw) => data. Throws on server
//                    error; the message is surfaced inline.
//   onSuccess      — optional callback fired AFTER a successful rotation.
//                    Used by manual mode to close + toast. Forced mode
//                    ignores it (AppGate unmounts the modal automatically
//                    once must_change_password flips to false).
//   onClose        — optional. Manual-mode dismiss handler (Esc /
//                    backdrop / Cancel). Ignored in forced mode.
//   errorMessage   — optional inline error from the caller.
//   onSignOut      — optional "Sign out instead" escape hatch (forced
//                    mode only; manual mode hides it since Cancel is
//                    a cleaner exit).
export default function ChangePasswordModal({
  open,
  mode = "forced",
  onSubmit,
  onSuccess,
  onClose,
  errorMessage,
  onSignOut,
}) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");
  // Step 7.8 (2026-02-15) — per-field password visibility toggle (Dave).
  // Three independent flags so revealing "Current" doesn't accidentally
  // reveal the new password mid-entry.
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const currentRef = useRef(null);
  const newRef = useRef(null);
  const confirmRef = useRef(null);
  const toggle = useCallback((setter, ref) => () => {
    setter((v) => !v);
    jumpCursorToStart(ref);
  }, []);

  // Step 7.7 (2026-02-15) — password policy sourced live from platform_settings
  // so a Platform Admin can tighten the rules from the Settings modal and
  // every subsequent ChangePassword flow reflects the new policy without a
  // frontend deploy.
  const { settings } = useSettings();
  const minLen = Math.max(1, Number(settings.password_min_length ?? 12));
  const needUpper = Number(settings.password_require_uppercase ?? 0) === 1;
  const needNumber = Number(settings.password_require_number ?? 0) === 1;
  const needSpecial = Number(settings.password_require_special ?? 0) === 1;

  const isManual = mode === "manual";

  // 2026-02-15 (Dave) — Manual mode: Esc closes the modal. Forced mode
  // intentionally has NO Esc handler.
  useEffect(() => {
    if (!open || !isManual) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, isManual, submitting, onClose]);

  // Reset fields whenever the modal transitions from closed → open. Prevents
  // stale values leaking between forced and manual invocations.
  useEffect(() => {
    if (open) {
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      setShowCurrent(false); setShowNew(false); setShowConfirm(false);
      setLocalError(""); setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  // Client-side policy — matches the backend so the user gets immediate
  // feedback before hitting the network.
  const meetsLength = newPw.length >= minLen;
  const hasUpper = /[A-Z]/.test(newPw);
  const hasNumber = /\d/.test(newPw);
  const hasSpecial = /[^A-Za-z0-9]/.test(newPw);
  const meetsUpper = !needUpper || hasUpper;
  const meetsNumber = !needNumber || hasNumber;
  const meetsSpecial = !needSpecial || hasSpecial;
  const differsFromCurrent = newPw.length > 0 && newPw !== currentPw;
  const matchesConfirm = newPw.length > 0 && newPw === confirmPw;
  const canSubmit =
    currentPw.length > 0 &&
    meetsLength &&
    meetsUpper &&
    meetsNumber &&
    meetsSpecial &&
    differsFromCurrent &&
    matchesConfirm &&
    !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setLocalError("");
    setSubmitting(true);
    try {
      await onSubmit(currentPw, newPw);
      // Manual mode: parent shows toast + closes. Forced mode: AppGate
      // unmounts us automatically when must_change_password flips.
      if (isManual) onSuccess?.();
    } catch (err) {
      setLocalError(err?.message || "Could not change your password.");
      setSubmitting(false);
    }
  };

  const displayError = localError || errorMessage;

  return (
    <div
      // Tinted halo backdrop — navy tint at low opacity + blur so the
      // Dashboard stays visible but clearly out-of-reach.
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{
        backgroundColor: "rgba(15, 42, 68, 0.28)",
        backdropFilter: "blur(1.5px)",
        WebkitBackdropFilter: "blur(1.5px)",
      }}
      data-testid="change-password-modal-backdrop"
      // Manual mode allows backdrop-dismiss; forced mode never dismisses.
      onClick={(e) => {
        if (!isManual || submitting) return;
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-2xl border border-rule w-[min(520px,94vw)] max-h-[92vh] flex flex-col rounded-[8px]"
        data-testid="change-password-modal"
      >
        <div
          className="px-6 pt-5 pb-3 relative"
          style={{ borderBottom: "1px solid #E4E7EB" }}
        >
          <div
            className="text-[15px] font-bold text-navy"
            data-testid="change-password-modal-title"
          >
            {isManual ? "Change Password" : "Change Your Password"}
          </div>
          <div className="text-[11.5px] text-slate mt-1 leading-snug pr-6">
            {isManual ? (
              <>
                Enter your current password and choose a new one. Any other
                devices where you&apos;re signed in will be signed out. Minimum{" "}
                {minLen} characters
                {needUpper || needNumber || needSpecial ? (
                  <>
                    {" · include "}
                    {[
                      needUpper && "uppercase",
                      needNumber && "number",
                      needSpecial && "special character",
                    ].filter(Boolean).join(", ")}
                  </>
                ) : null}
                , must differ from the current password.
              </>
            ) : (
              <>
                Your account is using a temporary password. Please choose a new
                one to continue. Minimum {minLen} characters
                {needUpper || needNumber || needSpecial ? (
                  <>
                    {" · include "}
                    {[
                      needUpper && "uppercase",
                      needNumber && "number",
                      needSpecial && "special character",
                    ].filter(Boolean).join(", ")}
                  </>
                ) : null}
                , must differ from the temporary password.
              </>
            )}
          </div>
          {isManual && (
            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              aria-label="Close change-password dialog"
              data-testid="change-password-close-btn"
              disabled={submitting}
              className="absolute top-3 right-3 text-slate hover:text-navy focus:outline-none focus:text-navy transition-colors rounded-[3px] p-1"
              style={{ cursor: submitting ? "not-allowed" : "pointer" }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="px-6 py-5 space-y-3.5">
          <div>
            <label
              className="block text-[10.5px] tracking-[0.12em] uppercase font-semibold text-navy mb-1"
              htmlFor="cp-current"
            >
              {isManual ? "Current Password" : "Current (Temporary) Password"}
            </label>
            <div className="relative">
              <input
                id="cp-current"
                ref={currentRef}
                type={showCurrent ? "text" : "password"}
                autoComplete="current-password"
                autoFocus
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="w-full border border-rule pl-3 pr-9 py-2 text-[13px] text-ink font-mono tabular-nums focus:outline-none focus:border-navy rounded-[3px]"
                data-testid="change-password-current"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={toggle(setShowCurrent, currentRef)}
                tabIndex={-1}
                aria-label={showCurrent ? "Hide current password" : "Show current password"}
                aria-pressed={showCurrent}
                data-testid="change-password-current-toggle"
                disabled={submitting}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate hover:text-navy focus:outline-none focus:text-navy transition-colors"
                style={{ cursor: submitting ? "not-allowed" : "pointer", padding: 2 }}
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label
              className="block text-[10.5px] tracking-[0.12em] uppercase font-semibold text-navy mb-1"
              htmlFor="cp-new"
            >
              New Password
            </label>
            <div className="relative">
              <input
                id="cp-new"
                ref={newRef}
                type={showNew ? "text" : "password"}
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="w-full border border-rule pl-3 pr-9 py-2 text-[13px] text-ink font-mono tabular-nums focus:outline-none focus:border-navy rounded-[3px]"
                data-testid="change-password-new"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={toggle(setShowNew, newRef)}
                tabIndex={-1}
                aria-label={showNew ? "Hide new password" : "Show new password"}
                aria-pressed={showNew}
                data-testid="change-password-new-toggle"
                disabled={submitting}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate hover:text-navy focus:outline-none focus:text-navy transition-colors"
                style={{ cursor: submitting ? "not-allowed" : "pointer", padding: 2 }}
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {newPw.length > 0 && (
              <div
                className="mt-1.5 text-[10.5px] leading-tight space-y-0.5"
                data-testid="change-password-policy"
              >
                <div style={{ color: meetsLength ? "#1C4E80" : "#EA6A47" }}>
                  {meetsLength ? "✓" : "•"} At least {minLen} characters
                </div>
                {needUpper && (
                  <div style={{ color: meetsUpper ? "#1C4E80" : "#EA6A47" }}>
                    {meetsUpper ? "✓" : "•"} Contains an uppercase letter
                  </div>
                )}
                {needNumber && (
                  <div style={{ color: meetsNumber ? "#1C4E80" : "#EA6A47" }}>
                    {meetsNumber ? "✓" : "•"} Contains a number
                  </div>
                )}
                {needSpecial && (
                  <div style={{ color: meetsSpecial ? "#1C4E80" : "#EA6A47" }}>
                    {meetsSpecial ? "✓" : "•"} Contains a special character
                  </div>
                )}
                <div
                  style={{
                    color: differsFromCurrent ? "#1C4E80" : "#EA6A47",
                  }}
                >
                  {differsFromCurrent ? "✓" : "•"} Differs from the
                  temporary password
                </div>
              </div>
            )}
          </div>

          <div>
            <label
              className="block text-[10.5px] tracking-[0.12em] uppercase font-semibold text-navy mb-1"
              htmlFor="cp-confirm"
            >
              Confirm New Password
            </label>
            <div className="relative">
              <input
                id="cp-confirm"
                ref={confirmRef}
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="w-full border border-rule pl-3 pr-9 py-2 text-[13px] text-ink font-mono tabular-nums focus:outline-none focus:border-navy rounded-[3px]"
                data-testid="change-password-confirm"
                disabled={submitting}
              />
              <button
                type="button"
                onClick={toggle(setShowConfirm, confirmRef)}
                tabIndex={-1}
                aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                aria-pressed={showConfirm}
                data-testid="change-password-confirm-toggle"
                disabled={submitting}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate hover:text-navy focus:outline-none focus:text-navy transition-colors"
                style={{ cursor: submitting ? "not-allowed" : "pointer", padding: 2 }}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirmPw.length > 0 && !matchesConfirm && (
              <div
                className="mt-1.5 text-[10.5px] text-coral"
                data-testid="change-password-confirm-mismatch"
              >
                • Confirmation does not match the new password
              </div>
            )}
          </div>
        </div>

        {displayError && (
          <div
            className="mx-6 mb-2 px-2.5 py-2 border border-coral bg-[#FEF5F2] text-coral text-[11px] leading-snug rounded-[3px]"
            data-testid="change-password-error"
          >
            {displayError}
          </div>
        )}

        <div
          className="px-6 py-4 flex items-center justify-between"
          style={{ borderTop: "1px solid #E4E7EB" }}
        >
          {isManual ? (
            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              className="text-[12px] text-slate hover:text-navy px-3 py-2 rounded-[3px] border border-rule bg-white transition-colors"
              data-testid="change-password-cancel-btn"
              disabled={submitting}
              style={{ cursor: submitting ? "not-allowed" : "pointer" }}
            >
              Cancel
            </button>
          ) : onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              className="text-[11px] text-slate hover:text-navy underline"
              data-testid="change-password-signout-btn"
              disabled={submitting}
            >
              Sign out instead
            </button>
          ) : (
            <span />
          )}
          <button
            type="submit"
            disabled={!canSubmit}
            className="text-white text-[13px] font-semibold px-5 py-2.5 rounded-[3px] transition-colors"
            style={
              canSubmit
                ? { backgroundColor: "#1C4E80", cursor: "pointer" }
                : { backgroundColor: "#B4BEC7", cursor: "not-allowed" }
            }
            data-testid="change-password-submit-btn"
          >
            {submitting ? "Updating…" : "Change Password"}
          </button>
        </div>
      </form>
    </div>
  );
}
